import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getColors, fonts } from '../theme';
import {
  ArrowLeft,
  TrendingUp,
  CheckSquare,
  ClipboardList,
  Target,
  Calculator,
  Zap,
  AlertTriangle,
  Scale,
  Award,
} from 'lucide-react';

const ScoringExplainerPage = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back button */}
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:opacity-80"
        style={{ color: colors.textMuted }}
      >
        <ArrowLeft size={16} />
        <span>Back to Dashboard</span>
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div 
            className="p-3 rounded-md"
            style={{ backgroundColor: `${colors.accent}20` }}
          >
            <TrendingUp size={24} style={{ color: colors.accent }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary, fontFamily: fonts.heading }}>
              How Your Productivity Score Works
            </h1>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              A deep dive into the algorithm behind your score
            </p>
          </div>
        </div>
      </div>

      {/* Overview */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textPrimary }}>
          <Zap size={18} style={{ color: colors.accent }} />
          Overview
        </h2>
        <div 
          className="pt-5 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <p className="mb-4 leading-relaxed" style={{ color: colors.textSecondary }}>
            Your Productivity Score is a <strong style={{ color: colors.textPrimary }}>weighted average</strong> of three components: 
            Habits, Tasks, and Goals. What makes it special is that the weights <em>adapt</em> based on what you actually track.
          </p>
          <p className="leading-relaxed" style={{ color: colors.textSecondary }}>
            If you're habit-focused, habits will impact your score more. If you're juggling many tasks, 
            tasks get a bigger slice. This prevents gaming the system and ensures your score reflects your <em>real</em> productivity.
          </p>
        </div>
      </section>

      {/* Formula */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textPrimary }}>
          <Calculator size={18} style={{ color: colors.accent }} />
          The Formula
        </h2>
        <div 
          className="p-5 rounded-lg font-mono text-sm"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
        >
          <div className="mb-4 p-4 rounded-md" style={{ backgroundColor: colors.bgSecondary }}>
            <p style={{ color: colors.accent }} className="text-center">
              Score = (Habit% × Weight<sub>H</sub>) + (Task% × Weight<sub>T</sub>) + (Goal% × Weight<sub>G</sub>)
            </p>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: colors.textMuted }}>
            Where each weight is dynamically calculated based on your usage, and the sum of all weights equals 100%.
          </p>
        </div>
      </section>

      {/* Weight Calculation */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textPrimary }}>
          <Scale size={18} style={{ color: colors.accent }} />
          Dynamic Weight Calculation
        </h2>
        <div 
          className="pt-5 pb-2 mb-4"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <p className="mb-4 leading-relaxed" style={{ color: colors.textSecondary }}>
            Weights are based on <strong style={{ color: colors.textPrimary }}>"effort units"</strong> for each category:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckSquare size={16} style={{ color: colors.accent }} className="mt-1 flex-shrink-0" />
              <div>
                <span className="font-medium" style={{ color: colors.textPrimary }}>Habits:</span>
                <span style={{ color: colors.textSecondary }}> Number of habits × 1.5</span>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  Higher multiplier because habits require daily attention
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ClipboardList size={16} style={{ color: colors.info }} className="mt-1 flex-shrink-0" />
              <div>
                <span className="font-medium" style={{ color: colors.textPrimary }}>Tasks:</span>
                <span style={{ color: colors.textSecondary }}> Number of active (incomplete) tasks</span>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  Only pending tasks count; completed tasks don't add weight pressure
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Target size={16} style={{ color: colors.textSecondary }} className="mt-1 flex-shrink-0" />
              <div>
                <span className="font-medium" style={{ color: colors.textPrimary }}>Goals:</span>
                <span style={{ color: colors.textSecondary }}> Total number of milestones across all goals</span>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  A goal with 5 milestones contributes more weight than a goal with 2
                </p>
              </div>
            </li>
          </ul>
        </div>
        
        <div 
          className="p-4 rounded-md flex items-start gap-3"
          style={{ backgroundColor: `${colors.warning}15`, border: `1px solid ${colors.warning}30` }}
        >
          <AlertTriangle size={18} style={{ color: colors.warning }} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>Balance Guardrails</p>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              To prevent any category from dominating, weights are <strong>clamped between 10% and 70%</strong>, 
              then re-normalized to sum to 100%.
            </p>
          </div>
        </div>
      </section>

      {/* Component Scores */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textPrimary }}>
          <Award size={18} style={{ color: colors.accent }} />
          Component Scores
        </h2>
        
        <div className="space-y-4">
          {/* Habits */}
          <div 
            className="pt-5 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckSquare size={18} style={{ color: colors.accent }} />
              <h3 className="font-medium" style={{ color: colors.textPrimary }}>Habit Score (0-100%)</h3>
            </div>
            <p className="mb-3 text-sm" style={{ color: colors.textSecondary }}>
              Calculated from your <strong>last 7 days</strong> of habit completions.
            </p>
            <div 
              className="p-3 rounded-md font-mono text-xs"
              style={{ backgroundColor: colors.bg }}
            >
              <p style={{ color: colors.accent }}>
                Habit Score = (Completions in 7 days) / (Habits × 7) × 100
              </p>
            </div>
            <div className="mt-3 p-3 rounded-md" style={{ backgroundColor: colors.bg }}>
              <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>Example:</p>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                You have <strong style={{ color: colors.textPrimary }}>3 habits</strong> and completed them 
                <strong style={{ color: colors.textPrimary }}> 16 times</strong> in the last 7 days.<br />
                Possible completions = 3 × 7 = 21<br />
                Score = 16/21 = <strong style={{ color: colors.accent }}>76%</strong><br />
                <span style={{ color: colors.textMuted }}>Effort units = 3 × 1.5 = <strong style={{ color: colors.accent }}>4.5</strong></span>
              </p>
            </div>
          </div>

          {/* Tasks */}
          <div 
            className="pt-5 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={18} style={{ color: colors.info }} />
              <h3 className="font-medium" style={{ color: colors.textPrimary }}>Task Score (0-100%)</h3>
            </div>
            <p className="mb-3 text-sm" style={{ color: colors.textSecondary }}>
              Based on tasks due or completed in the <strong>last 7 days</strong>, with overdue penalties.
            </p>
            <div 
              className="p-3 rounded-md font-mono text-xs"
              style={{ backgroundColor: colors.bg }}
            >
              <p style={{ color: colors.info }}>
                Task Score = (Completed Tasks / Total Tasks × 100) − Overdue Penalty
              </p>
            </div>
            <div className="mt-3 p-3 rounded-md" style={{ backgroundColor: colors.bg }}>
              <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>Example:</p>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                You had <strong style={{ color: colors.textPrimary }}>10 tasks</strong> this week, 
                completed <strong style={{ color: colors.textPrimary }}>7</strong>, 
                and have <strong style={{ color: colors.textPrimary }}>2 overdue</strong>.<br />
                Base = 7/10 = 70%<br />
                Penalty = 2 × 5% = 10%<br />
                Score = 70% − 10% = <strong style={{ color: colors.info }}>60%</strong><br />
                <span style={{ color: colors.textMuted }}>Effort units = 3 active tasks = <strong style={{ color: colors.info }}>3</strong></span>
              </p>
            </div>
            <div className="mt-3 p-3 rounded-md flex items-start gap-2" style={{ backgroundColor: `${colors.error}15` }}>
              <AlertTriangle size={14} style={{ color: colors.error }} className="mt-0.5" />
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                <strong>Overdue penalty:</strong> 5% per overdue task, max 25% penalty
              </p>
            </div>
          </div>

          {/* Goals */}
          <div 
            className="pt-5 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Target size={18} style={{ color: colors.textSecondary }} />
              <h3 className="font-medium" style={{ color: colors.textPrimary }}>Goal Score (0-100%)</h3>
            </div>
            <p className="mb-3 text-sm" style={{ color: colors.textSecondary }}>
              Based on <strong>milestone completions</strong> across all your active goals.
            </p>
            <div 
              className="p-3 rounded-md font-mono text-xs"
              style={{ backgroundColor: colors.bg }}
            >
              <p style={{ color: colors.textSecondary }}>
                Goal Score = (Completed Milestones / Total Milestones) × 100
              </p>
            </div>
            <div className="mt-3 p-3 rounded-md" style={{ backgroundColor: colors.bg }}>
              <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>Example:</p>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                Goal 1: "Learn Spanish": <strong style={{ color: colors.textPrimary }}>2/5</strong> milestones<br />
                Goal 2: "Run a marathon": <strong style={{ color: colors.textPrimary }}>3/4</strong> milestones<br />
                Total = 5 completed / 9 total = <strong style={{ color: colors.textSecondary }}>56%</strong><br />
                <span style={{ color: colors.textMuted }}>Effort units = 9 milestones = <strong style={{ color: colors.textSecondary }}>9</strong></span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Full Example */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textPrimary }}>
          <Calculator size={18} style={{ color: colors.accent }} />
          Full Calculation Example
        </h2>
        <div 
          className="pt-5 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <p className="mb-4 font-medium" style={{ color: colors.textPrimary }}>
            Let's say you have:
          </p>
          <ul className="space-y-2 mb-5 text-sm" style={{ color: colors.textSecondary }}>
            <li>• <span style={{ color: colors.accent }}>4 habits</span> → effort units = 4 × 1.5 = <strong style={{ color: colors.accent }}>6</strong></li>
            <li>• <span style={{ color: colors.info }}>3 active tasks</span> → effort units = <strong style={{ color: colors.info }}>3</strong></li>
            <li>• <span style={{ color: colors.textSecondary }}>2 goals with 7 total milestones</span> → effort units = <strong style={{ color: colors.textSecondary }}>7</strong></li>
          </ul>
          
          <div 
            className="p-4 rounded-md mb-4"
            style={{ backgroundColor: colors.bg }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Step 1: Calculate raw weights from effort units</p>
            <p className="text-sm font-mono" style={{ color: colors.textSecondary }}>
              Total effort units = <span style={{ color: colors.accent }}>6</span> + <span style={{ color: colors.info }}>3</span> + <span style={{ color: colors.textSecondary }}>7</span> = 16<br />
              <span style={{ color: colors.accent }}>Habits</span> = 6/16 = 37.5%<br />
              <span style={{ color: colors.info }}>Tasks</span> = 3/16 = 18.8%<br />
              <span style={{ color: colors.textSecondary }}>Goals</span> = 7/16 = 43.8%
            </p>
          </div>
          
          <div 
            className="p-4 rounded-md mb-4"
            style={{ backgroundColor: colors.bg }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Step 2: Apply clamping (10%-70%)</p>
            <p className="text-sm font-mono" style={{ color: colors.textSecondary }}>
              All values are within bounds, so no changes needed.
            </p>
          </div>
          
          <div 
            className="p-4 rounded-md mb-4"
            style={{ backgroundColor: colors.bg }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Step 3: Normalize to 100%</p>
            <p className="text-sm font-mono" style={{ color: colors.textSecondary }}>
              Final weights: <span style={{ color: colors.accent }}>Habits 38%</span>, 
              <span style={{ color: colors.info }}> Tasks 19%</span>, 
              <span style={{ color: colors.textSecondary }}> Goals 43%</span>
            </p>
          </div>
          
          <div 
            className="p-4 rounded-md"
            style={{ backgroundColor: `${colors.accent}10`, border: `1px solid ${colors.accent}30` }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Step 4: Calculate final score</p>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              With scores of <strong style={{ color: colors.accent }}>Habits 76%</strong>, 
              <strong style={{ color: colors.info }}> Tasks 60%</strong>, 
              <strong style={{ color: colors.textSecondary }}> Goals 56%</strong>:
            </p>
            <p className="text-lg font-mono mt-2" style={{ color: colors.accent }}>
              Score = (76 × 0.38) + (60 × 0.19) + (56 × 0.43) = <strong>64</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
          Tips to improve your score
        </h2>
        <div className="space-y-3">
          <div 
            className="p-4 rounded-md flex items-start gap-3"
            style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
          >
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <strong style={{ color: colors.textPrimary }}>Complete habits daily</strong>: they have the highest multiplier and reset every day
            </p>
          </div>
          <div 
            className="p-4 rounded-md flex items-start gap-3"
            style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
          >
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <strong style={{ color: colors.textPrimary }}>Don't let tasks go overdue</strong>: each overdue task costs you 5% off your task score
            </p>
          </div>
          <div 
            className="p-4 rounded-md flex items-start gap-3"
            style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
          >
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <strong style={{ color: colors.textPrimary }}>Break goals into milestones</strong>: more milestones = more granular progress tracking
            </p>
          </div>
          <div 
            className="p-4 rounded-md flex items-start gap-3"
            style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
          >
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <strong style={{ color: colors.textPrimary }}>Keep things balanced</strong>: the algorithm rewards consistent progress across all categories
            </p>
          </div>
        </div>
      </section>

      {/* Back to Dashboard */}
      <div className="text-center pt-6 border-t" style={{ borderColor: colors.border }}>
        <Link 
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-all "
          style={{ backgroundColor: colors.accent, color: colors.onAccent }}
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default ScoringExplainerPage;
